import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import localizedFormat from 'dayjs/plugin/localizedFormat';
// TODO get these in sync with the imports in CarrierQuote.tsx and link it to i18n language instead of browser locale
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en';
import 'dayjs/locale/cs';
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/hu';
import 'dayjs/locale/pl';
import 'dayjs/locale/ro';
import 'dayjs/locale/es';
import 'dayjs/locale/es-mx';
import { isNullOrUndefined } from '@utils/utils.tsx';

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(localizedFormat);
// Prefer navigator.languages / navigator.language, fall back to Intl and finally to 'cs'
export const browserLocale =
  (typeof navigator !== 'undefined' && navigator.language ||
    Intl.DateTimeFormat().resolvedOptions().locale ||
    'cs').toLowerCase();
console.log(`Setting Detected browser locale(s): ${browserLocale}`);
dayjs.locale(browserLocale);
dayjs.tz.setDefault('Europe/Prague');

/**
 *
 * @param date
 * @param shortYear
 * @deprecated obsolete-only — only used in utils.tsx via dead formatInterval wrapper.
 *   Use formatDateFromDayjsWithLocale from dateWithTz when cleaning up.
 */
export const dateFormatOnlyDateShort = (date: Dayjs, shortYear = false): string => {
  if (isNullOrUndefined(date)) return null;
  if (shortYear) {
    return date.format('D.M.YY');
  } else {
    return date.format('D.M.YYYY');
  }
};

/**
 * @deprecated dead — use stringDateTimeWithLocaleFromApiStringInTz(str, tz) from dateWithTz
 */
export const dateFormatDateTime = (date: Dayjs): string => {
  if (isNullOrUndefined(date)) return null;
  return date.format('DD.MM.YYYY, HH:mm');
};

/**
 *
 * @param previous
 * @param time
 * @deprecated obsolete-only — only used in InputDateAndTime2.tsx (no live page callers).
 *   Use dayjsWithSetTime(dayjs, time) from dateWithTz when cleaning up.
 */
export const setTime = (previous: Dayjs, time: string) => {
  if (isNullOrUndefined(time)) return previous;
  const splited = time.split(':');
  const newTime = previous.set('hour', parseInt(splited[0])).set('minute', parseInt(splited[1])).set('second', 0).set('millisecond', 0);
  return newTime;
};

/**
 * @deprecated live — RingilDataTable.tsx only.
 *   Use dayjsFromApiUtcInTz(str, tz) or dayjsFromApiPrague(str) from dateWithTz
 */
export const mapFromAPIDateTime = (stringDate: string): Dayjs => {
  if (isNullOrUndefined(stringDate)) return null;
  const date = dayjs(stringDate);
  //console.log(`stringDate: ${stringDate}, date: ${date} isValid: ${date.isValid()}, ${date.tz('Europe/Sofia')}`);
  return date.tz('Europe/Prague');
};

/**
 *
 * @param date
 * @deprecated dead — imported by utils.tsx but only via dead formatInterval/printTimeslotResponse wrappers.
 *   Use stringHHmmFromDayjs(dayjs) from dateWithTz.
 */
export const toTime = (date: Dayjs): string => {
  if (isNullOrUndefined(date)) return null;
  return date.format('HH:mm');
};

/**
 * @deprecated dead — all callers migrated (DuplicateExpeditionSidebar → stringHHmmFromStringInTz; utils.tsx only via dead getEventTimeData wrapper).
 *   Use stringHHmmFromStringInTz(str, tz) from dateWithTz
 */
export const mapFromAPIToTime = (date: string): string => {
  if (isNullOrUndefined(date)) return date;
  return mapFromAPIDateTime(date).tz('Europe/Prague').format('HH:mm');
};

/**
 * @deprecated dead — all callers migrated. AttachmentsList uses stringYMDFromStringInTz directly;
 *   CompanyExceptionWarning migrated (dates were local date strings → formatDateFromStringWithLocale).
 *   For UTC API strings use stringYMDFromStringInTz(str, tz) from dateWithTz.
 *   For local date strings (YYYY-MM-DD) use formatDateFromStringWithLocale(str) from dateWithTz.
 */
export const mapFromAPIToDateShort = (date: string): string => {
  if (isNullOrUndefined(date)) return date;
  return mapFromAPIDateTime(date).tz('Europe/Prague').format('DD.MM.YYYY');
};

// new functions


/**
 * replacement for mapFromAPIDateTime
 * @param stringDate
 * @param timezoneId
 * @deprecated dead — imported by utils.tsx but only via dead formatInterval/formatDateInterval wrappers.
 *   Use dayjsFromApiUtcInTz(str, tz) from dateWithTz.
 */
export const mapFromApiWithTz = (stringDate: string, timezoneId: string): Dayjs => {
  if (isNullOrUndefined(stringDate)) return null;
  const date = dayjs(stringDate);
  return date.tz(timezoneId);
};


// new new functions

/**
 * @deprecated obsolete-only — InputDateAndTime2.tsx (obsolete),
 *   utils.tsx via dead formatInterval wrapper. CompanyExceptionWarning migrated.
 *   Use formatTimeFromDayjsWithLocale(dayjs) from dateWithTz.
 */
export const dayJsParseHourAndMinutes = (date: Dayjs): string => {
  if (isNullOrUndefined(date)) return null;
  return date.format('LT');
};

